import {
  ErrorBoundary as PlatformErrorBoundary,
  SectionBoundary as PlatformSectionBoundary,
  WidgetErrorBoundary as PlatformWidgetErrorBoundary,
  PublicPageErrorBoundary as PlatformPublicPageErrorBoundary,
  type ErrorBoundaryProps,
  type SectionBoundaryProps,
  type WidgetErrorBoundaryProps,
  type PublicPageErrorBoundaryProps,
} from "@platform/core/ui";
import { Component, type ReactNode } from "react";
import { captureException, captureSectionError, sanitizeUrlForLogging } from "@/lib/sentry";

export type {
  ErrorBoundaryProps,
  SectionBoundaryProps,
  WidgetErrorBoundaryProps,
};

/* ── SectionBoundary — Sentry-wired ─────────────────────────────────────────── */

type SectionProps = {
  name: string;
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
};

/**
 * SectionBoundary
 *
 * Wraps @platform/core/ui SectionBoundary and injects captureSectionError
 * as the default onError handler when no explicit handler is provided.
 */
export class SectionBoundary extends Component<SectionProps> {
  render() {
    const { name, children, fallback, onError } = this.props;
    return (
      <PlatformSectionBoundary
        name={name}
        fallback={fallback}
        onError={
          onError ??
          ((error, errorInfo) =>
            captureSectionError(name, error, {
              componentStack: errorInfo.componentStack,
            }))
        }
      >
        {children}
      </PlatformSectionBoundary>
    );
  }
}

/* ── WidgetErrorBoundary — Sentry-wired ─────────────────────────────────────── */

type WidgetProps = {
  children: ReactNode;
  label?: string;
};

/**
 * WidgetErrorBoundary
 *
 * Wraps @platform/core/ui WidgetErrorBoundary and injects captureException
 * as the default onError handler.
 */
export class WidgetErrorBoundary extends Component<WidgetProps> {
  render() {
    const { children, label } = this.props;
    return (
      <PlatformWidgetErrorBoundary
        label={label}
        onError={(error, errorInfo) =>
          captureException(error, { componentStack: errorInfo.componentStack })
        }
      >
        {children}
      </PlatformWidgetErrorBoundary>
    );
  }
}

/* ── ErrorBoundary — Sentry-wired ───────────────────────────────────────────── */

type Props = {
  children: ReactNode;
};

/**
 * ErrorBoundary
 *
 * Full-page error boundary wrapping @platform/core/ui ErrorBoundary with
 * Sentry's captureException wired in.
 */
export class ErrorBoundary extends Component<Props> {
  render() {
    return (
      <PlatformErrorBoundary
        onError={(error, errorInfo) =>
          captureException(error, { componentStack: errorInfo.componentStack })
        }
      >
        {this.props.children}
      </PlatformErrorBoundary>
    );
  }
}

/* ── PublicPageErrorBoundary — Sentry-wired ─────────────────────────────────── */

type PublicProps = {
  children: ReactNode;
  route?: string;
  context?: string;
};

/**
 * PublicPageErrorBoundary
 *
 * Public-page error boundary wrapping @platform/core/ui PublicPageErrorBoundary
 * with Sentry wired in and the studio-config contact fetch pre-configured.
 */
export class PublicPageErrorBoundary extends Component<PublicProps> {
  render() {
    const { children, route, context } = this.props;
    return (
      <PlatformPublicPageErrorBoundary
        route={route ? sanitizeUrlForLogging(route) : undefined}
        context={context}
        onError={(error, errorInfo) =>
          captureException(error, {
            route: route ? sanitizeUrlForLogging(route) : undefined,
            context,
            componentStack: errorInfo.componentStack,
          })
        }
      >
        {children}
      </PlatformPublicPageErrorBoundary>
    );
  }
}
